// SS PWA — lib/copy/healthOutcomeCopy.ts — 2026-06-03
//
// LOCKED, single source of truth for the client-facing /health view.
//
// HARD RULES (per spec — locked Ernie 2026-06-03):
//   - Never expose anomaly rule names like OWNER_TAKEOVER_GATE_ERROR.
//   - Never expose cache hit rates, 429 counts, 502 counts, KV/D1 failures.
//   - Never expose worker errors, contact IDs of other people's customers,
//     or per-tenant cost data.
//   - When something's wrong, we say "we caught X issue and fixed it
//     automatically" — outcomes, not internals.
//
// Operator (Ernie's) admin view has its own copy — see AdminDiagnostics.tsx.
// That view CAN show internals.

export const healthCopy = {
  // Hero / status banner copy by status enum (matches HealthResponse.status).
  statusBanner: {
    green: {
      title: 'All systems normal',
      sub: 'Everything is running smoothly.',
      icon: 'check' as const,
    },
    yellow: {
      title: "We're watching something",
      sub: 'A few things are running a little slow but customers are getting through.',
      icon: 'eye' as const,
    },
    red: {
      title: 'We caught an issue',
      sub: "Our team is on it — your customers' messages are still being handled.",
      icon: 'wrench' as const,
    },
  },
  // Outcomes block.
  outcomes: {
    callsHandled: (n: number): string =>
      n === 1 ? '1 call handled this week' : `${n} calls handled this week`,
    callsHandled_sub:
      'Every customer who called got a fast, professional response.',
    smsResponseRate: (pct: number): string =>
      `${pct}% of customers got a response within 60 seconds`,
    smsResponseRate_sub:
      'Texts answered fast keep customers engaged.',
    quotesSent: (n: number): string =>
      n === 1 ? '1 quote sent' : `${n} quotes sent`,
    quotesSent_sub: 'Quotes delivered while the customer is still ready to buy.',
    reviewsRequested: (n: number): string =>
      n === 1 ? '1 review requested' : `${n} reviews requested`,
    reviewsRequested_sub:
      'Happy customers asked for a Google review after their job.',
    weeklyMomentum: 'Your week at a glance',
  },
  // When we DID catch something. Generic by design — no rule names.
  issueDigest: {
    none: 'No issues caught this week.',
    one: 'We caught 1 issue this week and fixed it automatically.',
    many: (n: number): string =>
      `We caught ${n} issues this week and fixed them automatically.`,
  },
  // Empty states.
  empty: {
    noActivity:
      "We're warming up — once your first call or text comes in, you'll see your snapshot here.",
    loading: 'Loading your snapshot…',
    error:
      "Couldn't load your snapshot right now. We're on it — try again in a minute.",
  },
  // Token capture (very rare — usually CF Access handles auth).
  tokenPrompt:
    'Enter your Service Steady access token to view your dashboard.',
} as const;

/**
 * Translate a HealthResponse status into a UI banner spec (color + label).
 * Used by HealthStatusBanner; isolated here so the COLOR-to-status mapping
 * matches the copy.
 */
export type HealthBannerSpec = {
  title: string;
  sub: string;
  tone: 'green' | 'yellow' | 'red';
  icon: 'check' | 'eye' | 'wrench';
};

export function bannerFor(status: 'green' | 'yellow' | 'red'): HealthBannerSpec {
  const t = healthCopy.statusBanner[status];
  return {
    title: t.title,
    sub: t.sub,
    tone: status,
    icon: t.icon,
  };
}

/**
 * Pick the right "we caught N issues" line. n=0 returns the green line;
 * n>0 returns either the singular or plural variant.
 */
export function issueDigestFor(n: number): string {
  if (n <= 0) return healthCopy.issueDigest.none;
  if (n === 1) return healthCopy.issueDigest.one;
  return healthCopy.issueDigest.many(n);
}
